import { Edit3, Power, RotateCcw } from 'lucide-react';
import ActionButton from '../../../../components/Buttons/ActionButton';
import {
  RestaurantTableDTO,
  RestaurantTableShape,
  RestaurantTableStatus,
} from '../../../../types/restaurant/RestaurantTableTypes';
import { tableStatusOptions } from './TableCard';
import TableSelect from '../../../../components/Forms/Input/TableSelect';
import TableShapeIcon from './TableShapeIcon';

interface RestaurantTableListProps {
  tables: RestaurantTableDTO[];
  onEdit: (table: RestaurantTableDTO) => void;
  onStatusChange: (
    table: RestaurantTableDTO,
    status: RestaurantTableStatus
  ) => void;
  onActiveChange: (table: RestaurantTableDTO, active: boolean) => void;
}

const shapeLabels: Record<RestaurantTableShape, string> = {
  SQUARE: 'Cuadrada',
  RECTANGLE: 'Rectangular',
  ROUND: 'Redonda',
};

const RestaurantTableList = ({
  tables,
  onEdit,
  onStatusChange,
  onActiveChange,
}: RestaurantTableListProps) => (
  <div className="hidden overflow-x-auto md:block">
    <table className="w-full min-w-[1120px] table-auto">
      <thead>
        <tr className="border-b border-stroke bg-gray-2/60 text-left text-xs font-semibold uppercase tracking-wide text-bodydark2 dark:border-strokedark dark:bg-meta-4/30">
          <th className="px-4 py-4 text-center">Orden</th>
          <th className="px-4 py-4">Código</th>
          <th className="px-4 py-4">Nombre</th>
          <th className="px-4 py-4">Salón</th>
          <th className="px-4 py-4 text-center">Capacidad</th>
          <th className="px-4 py-4">Forma</th>
          <th className="px-4 py-4">Estado operativo</th>
          <th className="px-4 py-4">Activa/Inactiva</th>
          <th className="px-4 py-4 text-right">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {tables.map((table) => (
          <tr
            key={table.id}
            className={`border-b border-stroke last:border-0 dark:border-strokedark ${
              table.active ? '' : 'opacity-65'
            }`}
          >
            <td className="px-4 py-4 text-center font-semibold text-black dark:text-white">
              {table.displayOrder}
            </td>
            <td className="whitespace-nowrap px-4 py-4 font-semibold text-primary">
              {table.code}
            </td>
            <td className="px-4 py-4 font-medium text-black dark:text-white">
              {table.name}
            </td>
            <td className="px-4 py-4 text-sm text-bodydark2">
              {table.diningRoomName || 'Sin salón'}
            </td>
            <td className="px-4 py-4 text-center text-black dark:text-white">
              {table.capacity}
            </td>
            <td className="px-4 py-4 text-sm text-bodydark2">
              <span className="inline-flex items-center gap-2">
                <TableShapeIcon
                  shape={table.shape}
                  capacity={table.capacity}
                  className="h-9 w-9 shrink-0 text-primary"
                />
                {shapeLabels[table.shape]}
              </span>
            </td>
            <td className="px-4 py-3">
              <TableSelect
                value={table.status}
                disabled={!table.active}
                className="min-w-[155px]"
                options={tableStatusOptions}
                onChange={(event) =>
                  onStatusChange(
                    table,
                    event.target.value as RestaurantTableStatus
                  )
                }
              />
            </td>
            <td className="px-4 py-4">
              <span
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                  table.active
                    ? 'border-success/20 bg-success/10 text-success'
                    : 'border-danger/20 bg-danger/10 text-danger'
                }`}
              >
                {table.active ? 'Activa' : 'Inactiva'}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="flex justify-end gap-2">
                <ActionButton
                  label="Editar"
                  icon={<Edit3 className="h-4 w-4" />}
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => onEdit(table)}
                />
                <ActionButton
                  label={table.active ? 'Desactivar' : 'Reactivar'}
                  icon={
                    table.active ? (
                      <Power className="h-4 w-4" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )
                  }
                  variant={table.active ? 'danger' : 'primary'}
                  size="sm"
                  fullWidth={false}
                  onClick={() => onActiveChange(table, !table.active)}
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default RestaurantTableList;
